import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Search, X } from 'lucide-react';

import { TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Which action verbs the audit log currently emits — used to populate the
// filter dropdown. Keep in sync with log_admin_event() call sites.
const ACTION_VERBS = [
  'user.delete',
  'user.bulk_delete',
  'user.role_update',
  'user.self_delete',
];

const ACTION_LABELS = {
  'user.delete': { label: 'User Deleted', color: 'bg-red-500/20 text-red-500' },
  'user.bulk_delete': { label: 'Users Bulk Deleted', color: 'bg-red-500/20 text-red-500' },
  'user.role_update': { label: 'Role Updated', color: 'bg-blue-500/20 text-blue-500' },
  'user.self_delete': { label: 'Account Self-Deleted', color: 'bg-orange-500/20 text-orange-500' },
};

const PAGE_SIZE = 50;

const AuditTab = ({ token }) => {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const [filterAction, setFilterAction] = useState('all');
  const [searchTargetId, setSearchTargetId] = useState('');

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip };
      if (filterAction !== 'all') params.action = filterAction;
      if (searchTargetId.trim()) params.target_id = searchTargetId.trim();
      const res = await axios.get(`${API}/admin/audit`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setEvents(res.data.events || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Audit fetch error:', err);
      toast.error('Could not load audit log');
    } finally {
      setLoading(false);
    }
  }, [token, skip, filterAction, searchTargetId]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const resetFilters = () => {
    setFilterAction('all');
    setSearchTargetId('');
    setSkip(0);
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const renderDetails = (event) => {
    const d = event.details || {};
    if (event.action === 'user.delete' || event.action === 'user.self_delete') {
      return (
        <div className="text-xs text-muted-foreground">
          <div>Email: {d.target_email || '—'}</div>
          <div>Cascade: {Object.entries(d.cascade || {}).map(([k, v]) => `${k}:${v}`).join(', ')}</div>
        </div>
      );
    }
    if (event.action === 'user.bulk_delete') {
      return (
        <div className="text-xs text-muted-foreground">
          <div>Deleted: {(d.deleted_ids || []).length} / requested {d.requested || 0}</div>
          {d.skipped?.length > 0 && <div>Skipped: {d.skipped.length}</div>}
        </div>
      );
    }
    if (event.action === 'user.role_update') {
      return (
        <div className="text-xs text-muted-foreground">
          <div>Email: {d.target_email || '—'}</div>
          <div>
            {d.before?.admin_role || 'none'} → {d.after?.admin_role || 'none'}
          </div>
        </div>
      );
    }
    return <pre className="text-xs text-muted-foreground max-w-md overflow-auto">{JSON.stringify(d, null, 0)}</pre>;
  };

  return (
    <TabsContent value="audit" className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="flex-1">
          <h2 className="text-xl font-semibold">Audit Log</h2>
          <p className="text-sm text-muted-foreground">
            Every consequential admin action, newest first. Append-only.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchEvents}
          disabled={loading}
          data-testid="audit-refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Select
          value={filterAction}
          onValueChange={(v) => {
            setFilterAction(v);
            setSkip(0);
          }}
        >
          <SelectTrigger className="sm:w-64" data-testid="audit-filter-action">
            <SelectValue placeholder="Filter by action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {ACTION_VERBS.map((v) => (
              <SelectItem key={v} value={v}>
                {ACTION_LABELS[v]?.label || v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Filter by target user_id or entity_id"
            value={searchTargetId}
            onChange={(e) => setSearchTargetId(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setSkip(0);
                fetchEvents();
              }
            }}
            data-testid="audit-search-target"
          />
        </div>
        {(filterAction !== 'all' || searchTargetId) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={resetFilters}
            data-testid="audit-clear-filters"
          >
            <X className="h-3 w-3 mr-1" /> Clear
          </Button>
        )}
      </div>

      <div className="rounded-xl border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Target</TableHead>
              <TableHead>Details</TableHead>
              <TableHead>IP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  {loading ? 'Loading…' : 'No audit events yet'}
                </TableCell>
              </TableRow>
            ) : (
              events.map((e) => {
                const meta = ACTION_LABELS[e.action] || { label: e.action, color: 'bg-muted' };
                return (
                  <TableRow key={e.event_id} data-testid={`audit-row-${e.event_id}`}>
                    <TableCell className="text-sm whitespace-nowrap">{formatDate(e.created_at)}</TableCell>
                    <TableCell className="text-sm">
                      <div className="font-medium">{e.actor_email || '—'}</div>
                      <div className="text-xs text-muted-foreground">{e.actor_user_id}</div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${meta.color} border-0`}>{meta.label}</Badge>
                    </TableCell>
                    <TableCell className="text-sm font-mono text-xs">{e.target_id || '—'}</TableCell>
                    <TableCell>{renderDetails(e)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{e.ip || '—'}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div>
            Showing {skip + 1}–{Math.min(skip + events.length, total)} of {total}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={skip === 0 || loading}
              onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              data-testid="audit-prev-page"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={skip + PAGE_SIZE >= total || loading}
              onClick={() => setSkip(skip + PAGE_SIZE)}
              data-testid="audit-next-page"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </TabsContent>
  );
};

export default AuditTab;
